
import React from 'react';
import { motion } from 'framer-motion';
import { BarChart as BarChartIcon, Clock, Sparkles, FileText } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Sample sleep data for the visualization
const sleepData = [
  { time: '8PM', sleepQuality: 20 },
  { time: '9PM', sleepQuality: 40 },
  { time: '10PM', sleepQuality: 95 },
  { time: '11PM', sleepQuality: 90 },
  { time: '12AM', sleepQuality: 85 },
  { time: '1AM', sleepQuality: 75 },
  { time: '2AM', sleepQuality: 50 },
  { time: '3AM', sleepQuality: 70 },
  { time: '4AM', sleepQuality: 90 },
  { time: '5AM', sleepQuality: 95 },
  { time: '6AM', sleepQuality: 80 },
];

export function DashboardPreviewSection() {
  return <section className="py-24 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-indigo-50 rounded-br-[100px] opacity-70"></div>
      <div className="absolute bottom-0 right-0 w-1/4 h-1/4 bg-purple-50 rounded-tl-[100px] opacity-70"></div>
      
      <div className="container mx-auto px-4">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
            Finally, A Tool Designed for Sleep Consultants
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            SleepNest provides the insights and tools you need to help families sleep better, faster.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left Column - Features */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[{
              icon: <Sparkles className="h-5 w-5 text-indigo-600" />,
              title: "AI-Powered Sleep Analysis",
              description: "Get automatic insights from parent logs."
            }, {
              icon: <Clock className="h-5 w-5 text-purple-600" />,
              title: "Real-Time Parent Updates",
              description: "No more chasing for information."
            }, {
              icon: <BarChartIcon className="h-5 w-5 text-blue-600" />,
              title: "Pattern Recognition",
              description: "Instantly see sleep disruptors."
            }, {
              icon: <FileText className="h-5 w-5 text-emerald-600" />,
              title: "Professional Reports",
              description: "Save hours per client with structured summaries."
            }].map((feature, index) => <motion.div key={index} whileHover={{
              y: -5
            }} className="p-6 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex-shrink-0 bg-indigo-50 p-2 rounded-full">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold">{feature.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </motion.div>)}
            </div>
          </motion.div>

          {/* Right Column - Dashboard Preview */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative">
            {/* Full Dashboard Preview */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }}>
              <Card className="overflow-hidden shadow-lg border-indigo-100">
                <CardContent className="p-6 bg-zinc-100">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Sleep Analysis Dashboard</h3>
                      <p className="text-sm text-gray-700 font-medium">Client: Emma Thompson, 7 months</p>
                    </div>
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                      <FileText className="h-4 w-4 mr-1" />
                      Generate Report
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                      <div className="text-sm font-medium text-indigo-900 mb-1">Average Sleep</div>
                      <div className="text-2xl font-bold text-indigo-700">9.2 hrs</div>
                      <div className="text-xs text-indigo-600 mt-1">▲ 8% from last week</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                      <div className="text-sm font-medium text-green-900 mb-1">Night Wakings</div>
                      <div className="text-2xl font-bold text-green-700">1.3/night</div>
                      <div className="text-xs text-green-600 mt-1">▼ 40% from last week</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                      <div className="text-sm font-medium text-purple-900 mb-1">Sleep Quality</div>
                      <div className="text-2xl font-bold text-purple-700">Good</div>
                      <div className="text-xs text-purple-600 mt-1">Improved restfulness</div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-4">
                    <div className="flex items-center mb-3">
                      <Sparkles className="h-4 w-4 text-indigo-600 mr-2" />
                      <div className="text-sm font-medium">AI Insight</div>
                    </div>
                    <p className="text-sm text-gray-700">
                      Sleep disruptions occur most frequently between 2-3 AM, often following late afternoon naps. 
                      Consider adjusting the afternoon nap schedule to end before 4 PM.
                    </p>
                  </div>
                  
                  {/* Replaced placeholder with actual chart */}
                  <div className="h-48 bg-white rounded-lg border border-gray-200 p-2">
                    <div className="text-sm font-medium text-gray-700 mb-2">Sleep Pattern Visualization</div>
                    <ResponsiveContainer width="100%" height="85%">
                      <AreaChart
                        data={sleepData}
                        margin={{ top: 0, right: 5, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="time" 
                          tick={{ fontSize: 10, fill: '#666' }}
                          tickLine={false}
                        />
                        <YAxis 
                          hide={true}
                          domain={[0, 100]}
                        />
                        <Tooltip 
                          formatter={(value) => [`${value}% Quality`, 'Sleep']}
                          labelFormatter={(label) => `Time: ${label}`}
                          contentStyle={{ fontSize: '11px' }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="sleepQuality" 
                          stroke="#7c3aed" 
                          fillOpacity={0.8}
                          fill="url(#colorSleep)" 
                        />
                        <defs>
                          <linearGradient id="colorSleep" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}
